import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { debounce } from "lodash";
import axiosInstance, { BASE_URL } from "../../../utils/config/config";
import { getIPHCPractioner } from "../../../redux/actions/user.dispatcher";
import { getInitialSubmitData } from "./Nirva.utils";
import { AUTOSAVE_DELAY } from "./Nirva.constants";

export const useNirvaPractitioner = () => {
  const dispatch = useDispatch();
  const { iphcPractitioner, userData } = useSelector((state) => state.user);

  const [practitionerModalState, setPractitionerModalState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disclaimerModalState, setDisclaimerModalState] = useState(false);
  const [modalState, setModalState] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorState, setErrorState] = useState({ error: false, message: "" });
  const [autoSaveStatus, setAutoSaveStatus] = useState("");
  const [submitData, setSubmitData] = useState(
    getInitialSubmitData(iphcPractitioner)
  );
  const [, setLocalFiles] = useState({});
  const [, setIphcPractitionerData] = useState({});

  const draftKey = userData?.id ? `iphc_draft_${userData.id}` : null;
  const isModalOpenRef = useRef(false);
  const hasRestoredDraft = useRef(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    isModalOpenRef.current = modalState;
  }, [modalState]);

  useEffect(() => {
    if (!draftKey || hasRestoredDraft.current) return;
    hasRestoredDraft.current = true;

    try {
      const stored = localStorage.getItem(draftKey);
      if (!stored) return;

      const { data: draftData, timestamp } = JSON.parse(stored);
      if (!draftData) return;

      const serverUpdated = iphcPractitioner?.updatedAt
        ? new Date(iphcPractitioner.updatedAt).getTime()
        : 0;

      if (timestamp > serverUpdated) {
        // Ensure agePreferences from older drafts (stored as string) is converted to array
        if (draftData.agePreferences && typeof draftData.agePreferences === "string") {
          draftData.agePreferences = draftData.agePreferences
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        }
        setSubmitData((prev) => ({ ...prev, ...draftData }));
        const savedTime = new Date(timestamp).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
        setAutoSaveStatus(`Draft restored from ${savedTime}`);
      }
    } catch (err) {
      console.error("Error restoring autosave draft:", err);
    }
  }, [draftKey, iphcPractitioner]);

  const debouncedSave = useMemo(
    () =>
      debounce((data, key) => {
        if (!key) return;
        try {
          localStorage.setItem(
            key,
            JSON.stringify({ data, timestamp: Date.now() })
          );
          const now = new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });
          setAutoSaveStatus(`Draft saved at ${now}`);
        } catch (err) {
          console.error("Error saving draft to localStorage:", err);
        }
      }, AUTOSAVE_DELAY),
    []
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!isModalOpenRef.current || !draftKey) return;

    setAutoSaveStatus("Saving...");
    debouncedSave(submitData, draftKey);

    return () => debouncedSave.cancel();
  }, [submitData, draftKey, debouncedSave]);

  const clearDraft = useCallback(() => {
    if (draftKey) {
      localStorage.removeItem(draftKey);
      setAutoSaveStatus("");
    }
  }, [draftKey]);

  useEffect(() => {
    if (iphcPractitioner) {
      const copy = JSON.parse(JSON.stringify(iphcPractitioner));
      setIphcPractitionerData(copy);

      try {
        const stored = draftKey ? localStorage.getItem(draftKey) : null;
        if (stored) {
          const { timestamp } = JSON.parse(stored);
          const serverUpdated = iphcPractitioner.updatedAt
            ? new Date(iphcPractitioner.updatedAt).getTime()
            : 0;
          if (timestamp > serverUpdated) return; // draft is newer, keep it
        }
      } catch (err) {
        console.error("Error checking draft during server data load:", err);
      }

      setSubmitData(getInitialSubmitData(copy));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iphcPractitioner]);

  const handleSubmit = async (dataToSubmit) => {
    try {
      setLoading(true);

      const data = dataToSubmit ?? submitData;
      const payload = {
        ...data,
        agePreferences: Array.isArray(data.agePreferences)
          ? data.agePreferences.join(", ")
          : data.agePreferences,
      };

      const response = await axiosInstance.post(
        "user/create-iphc-practitioner",
        payload
      );

      if (response.status >= 200 && response.status < 300) {
        // Delay re-fetch to allow GHL propagation before reading back
        setTimeout(() => dispatch(getIPHCPractioner()), 1500);
        clearDraft();
        setSuccess(true);
        return true;
      }
      return false;
    } catch (err) {
      setErrorState({ error: true, message: "Failed to save the data" });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e, fieldName, isMultiple = false) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      const uploadedUrls = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);

        const res = await axios.post(`${BASE_URL}aws/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        uploadedUrls.push(res.data.fileUrl);
      }

      setSubmitData((prev) => ({
        ...prev,
        [fieldName]: isMultiple
          ? [...(prev[fieldName] || []), ...uploadedUrls]
          : uploadedUrls[0],
      }));

      setLocalFiles((prev) => ({
        ...prev,
        [fieldName]: isMultiple
          ? [...(prev[fieldName] || []), ...uploadedUrls]
          : uploadedUrls[0],
      }));

      e.target.value = "";
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const handleFileDelete = (fieldName, index) => {
    setSubmitData((prev) => {
      const updated = Array.isArray(prev[fieldName])
        ? prev[fieldName].filter((_, i) => i !== index)
        : null;
      return { ...prev, [fieldName]: updated };
    });

    setLocalFiles((prev) => {
      const updated = Array.isArray(prev[fieldName])
        ? prev[fieldName].filter((_, i) => i !== index)
        : null;
      return { ...prev, [fieldName]: updated };
    });
  };

  const handleImageUpload = async (file, fieldName) => {
    const uploadData = new FormData();
    uploadData.append("file", file);

    try {
      setLoading(true);
      const response = await axios.post(`${BASE_URL}aws/upload`, uploadData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const uploadedUrl = response.data.fileUrl;
      setSubmitData((prev) => ({ ...prev, [fieldName]: uploadedUrl }));
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleDisclaimerSave = () => {
    setDisclaimerModalState(false);
    setModalState(true);
  };

  return {
    loading,
    success,
    errorState,
    autoSaveStatus,
    submitData,
    setSubmitData,
    iphcPractitioner,
    practitionerModalState,
    setPractitionerModalState,
    disclaimerModalState,
    setDisclaimerModalState,
    modalState,
    setModalState,
    setErrorState,
    setSuccess,
    handleSubmit,
    handleFileChange,
    handleFileDelete,
    handleImageUpload,
    handleDisclaimerSave,
  };
};
